import { useRef, KeyboardEvent } from "react";

import { Icone } from "@componentes-mu/componentes-mu";

import styles from "./styles.module.scss";

export type InputFileProps = {
  files?: File[];
  id?: string;
  label?: string;
  style?: React.CSSProperties;
  multiple?: boolean;
  maxFileSizeMB?: number;
  onFilesChange: (files: File[]) => void;
  onRemoveFile: (index: number) => void;
  onClearAll?: () => void;
};

export const InputFile = ({
  id,
  label = "Selecionar arquivo(s)",
  style,
  files = [],
  multiple = true,
  maxFileSizeMB = 10,
  onFilesChange,
  onRemoveFile,
  onClearAll,
}: InputFileProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const hasFiles = files.length > 0;

  const openPicker = () => inputRef.current?.click();

  const onKeyActivate = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openPicker();
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? []);
    if (picked.length === 0) return;

    const maxBytes = maxFileSizeMB * 1024 * 1024;
    const oversized = picked.filter(f => f.size > maxBytes);
    if (oversized.length > 0) {
      const names = oversized.map(f => f.name).join(", ");
      alert(`Os seguintes arquivos excedem o limite de ${maxFileSizeMB}MB e não podem ser anexados:\n${names}`);
      event.target.value = "";
      return;
    }

    if (multiple) {
      const existing = new Map(files.map(file => [`${file.name}-${file.size}`, file]));

      for (const file of picked) {
        const key = `${file.name}-${file.size}`;
        existing.set(key, file);
      }

      onFilesChange(Array.from(existing.values()));
    } else {
      onFilesChange([picked[0]]);
    }

    event.target.value = "";
  };

  return (
    <div style={style}>
      <input
        ref={inputRef}
        className={styles.hiddenInput}
        onChange={handleChange}
        accept=".pdf,.xlsx,.html,.docx,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.bmp,.webp,.svg"
        type="file"
        id={id}
        multiple={multiple}
      />

      <div
        className={`${styles.inputContainer} ${hasFiles ? styles.filled : ""}`}
        onClick={openPicker}
        onKeyDown={onKeyActivate}
        role="button"
        tabIndex={0}
        aria-label={hasFiles ? "Adicionar mais arquivos" : "Selecionar arquivo(s)"}
      >
        <div className={`${styles.displayArea} ${hasFiles ? styles.displayAreaFilled : ""}`}>
          <Icone nome="attach_file" largura="20px" altura="20px" cor="#FFFFFF" />
          <span className={styles.badge}>
            {hasFiles ? `${files.length} arquivo(s) selecionado(s)` : label}
          </span>

          {hasFiles && (
            <button
              type="button"
              className={styles.clearButton}
              title="Limpar todos"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClearAll?.();
              }}
            >
              <Icone nome="delete" largura="18px" altura="18px" cor="#D64545" />
            </button>
          )}
        </div>
      </div>

      {hasFiles && (
        <ul className={styles.fileList}>
          {files.map((file, index) => (
            <li key={`${file.name}-${file.size}`} className={styles.fileItem}>
              <div className={styles.fileLeft}>
                <Icone nome="image" largura="16px" altura="16px" cor="#4A6372" />
                <span className={styles.fileName}>{file.name}</span>
              </div>
              <button
                type="button"
                className={styles.removeItemBtn}
                onClick={(e) => {
                  e.preventDefault();
                  onRemoveFile(index);
                }}
              >
                <Icone nome="close" largura="16px" altura="16px" cor="#D64545" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>

  );
};
